import type { FunctionComponent } from 'react'

import {
	Accordion,
	AccordionItem,
	BodyL,
	BORDER_RADIUS,
	COLORS,
	Link,
	Modal,
	ModalContent,
	ModalDescription,
	ModalFooter,
	ModalHeader,
	ModalTitle,
	ModalWrapper,
	PoweredByMonthio,
	SecondaryButton,
	SPACING,
	TertiaryButton,
	TertiaryIconButton,
	TYPOGRAPHY,
	useModal,
} from '@monthio/design-system'
import { Close, MoreVert } from '@monthio/icons'
import { useCallback } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { styled } from 'styled-components'

import { intercomService } from '../../services/intercomService'
import { isIntercomEnabledForSession } from '../../utils/intercom'
import { DeleteDataContent } from './DeleteDataContent'

interface KebabMenuButtonProps {
	applicantId?: string | null
	clientId?: number
}

export const KebabMenuButton: FunctionComponent<KebabMenuButtonProps> = ({
	applicantId,
	clientId,
}) => {
	const { t } = useTranslation()
	return (
		<Modal
			trigger={
				<TertiaryIconButton
					ariaLabel={t('kebabMenu-trigger-ariaLabel')}
					icon={<MoreVert aria-hidden="true" />}
					size="xl"
				/>
			}
		>
			<KebabMenuModal applicantId={applicantId} clientId={clientId} />
		</Modal>
	)
}

interface KebabMenuProps {
	applicantId?: string | null
	clientId?: number
}

const KebabMenuModal: FunctionComponent<KebabMenuProps> = ({
	applicantId,
	clientId,
}) => {
	const { close } = useModal()
	const { t } = useTranslation()
	return (
		<ModalWrapper>
			<ModalHeader>
				<TertiaryIconButton
					ariaLabel={t('kebabMenu-closeBtn-ariaLabel')}
					icon={<Close aria-hidden="true" />}
					onClick={close}
					size="xl"
				/>
			</ModalHeader>
			<ModalContent>
				<KebabMenuMainContent>
					<ModalTitle>{t('kebabMenu-title')}</ModalTitle>
					<KebabMenuFaqContent />
				</KebabMenuMainContent>
				<KebabMenuBottomContent>
					<ContactSupport
						applicantId={applicantId}
						clientId={clientId}
						close={close}
					/>
					<PoweredByMonthioContainer />
				</KebabMenuBottomContent>
			</ModalContent>
			<ModalFooter>
				{/* this button is hidden from a screen reader,
        			because we want to provide a single clear "Close" dialog button. */}
				<TertiaryButton aria-hidden="true" onClick={close} size="xl">
					{t('btn-close')}
				</TertiaryButton>
			</ModalFooter>
		</ModalWrapper>
	)
}

const KebabMenuMainContent = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${SPACING.l};
	flex-grow: 1;
`

const KebabMenuBottomContent = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${SPACING.xxl};
	padding-block-start: ${SPACING.m};
`

const DeleteContentWrapper = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${SPACING.s};
`

const KebabMenuFaqContent: FunctionComponent = () => {
	const { t } = useTranslation()
	const { close } = useModal()

	return (
		<Accordion>
			<AccordionItem
				id="kebabMenu-faq-title"
				spacing="m"
				title={t('kebabMenu-faq-title')}
			>
				<ModalDescription $spacing="xl">
					{t('kebabMenu-faq-subMenu-subtitle')}
				</ModalDescription>
				<Accordion>
					{[...Array(8).keys()].map((i) => {
						const questionNumber = i + 1
						return (
							<AccordionItem
								headingLevel={3}
								spacing="xxl"
								key={`FAQ::QUESTION_${questionNumber}`}
								id={`FAQ::QUESTION_${questionNumber}`}
								title={t(`kebabMenu-faq-question${questionNumber}`)}
							>
								<BodyL>
									<Trans
										t={t}
										i18nKey={`kebabMenu-faq-answer${questionNumber}`}
										components={{
											linkTag: (
												<Link
													target="_blank"
													href="https://www.monthio.dk/terms"
												/>
											),
										}}
									/>
								</BodyL>
							</AccordionItem>
						)
					})}
				</Accordion>
			</AccordionItem>
			<AccordionItem
				id="kebabMenu-privacyPolicy-title"
				spacing="m"
				title={t('kebabMenu-privacyPolicy-title')}
			>
				<BodyL>
					<Trans
						i18nKey="kebabMenu-privacyPolicy-text"
						components={{
							linkTag: (
								<Link target="_blank" href="https://www.monthio.dk/terms" />
							),
						}}
					/>
				</BodyL>
			</AccordionItem>
			<AccordionItem
				id="kebabMenu-deleteData-title"
				spacing="m"
				title={t('kebabMenu-deleteData-title')}
			>
				<DeleteContentWrapper>
					<BodyL>{t('kebabMenu-deleteData-subMenu-subtitle')}</BodyL>
					<DeleteDataContent handleCloseMenu={close} />
				</DeleteContentWrapper>
			</AccordionItem>
		</Accordion>
	)
}

const PoweredByMonthioContainer = styled(PoweredByMonthio)`
	width: 100%;
`

interface ContactSupportProps {
	close: () => void
	applicantId?: string | null
	clientId?: number
}
const ContactSupport: FunctionComponent<ContactSupportProps> = ({
	close,
	applicantId,
	clientId,
}) => {
	const { t } = useTranslation()

	const handleContactSupport = useCallback(() => {
		close()
		intercomService.show()
	}, [close])

	const supportId = `${applicantId?.slice(0, 4)}-${applicantId?.slice(4, 8)}`

	return (
		<ContactSupportWrapper>
			{applicantId ? (
				<ContactSupportId>
					<InfoLabel>{t('kebabMenu-supportId-text')}:</InfoLabel> {supportId}
				</ContactSupportId>
			) : null}
			{clientId === undefined || isIntercomEnabledForSession(clientId) ? (
				<SecondaryButton onClick={handleContactSupport} size="xl">
					{t('kebabMenu-contactSupport-title')}
				</SecondaryButton>
			) : (
				<SupportContactInformation supportId={supportId} />
			)}
		</ContactSupportWrapper>
	)
}

const ContactSupportWrapper = styled.div`
	width: 100%;
	background-color: ${COLORS.primitives.primaryBrand._100};
	border: 1px solid ${COLORS.primitives.primaryBrand._300};
	border-radius: ${BORDER_RADIUS.m};
	padding: ${SPACING.m};
	box-sizing: border-box;
	margin-top: auto;

	> button {
		width: 100%;
	}
`
const ContactSupportId = styled.span`
	display: block;
	font: ${TYPOGRAPHY.bodyLStrong};
	margin-bottom: ${SPACING.l};
`

const InfoLabel = styled.span`
	${TYPOGRAPHY.bodyLStrong};
	color: ${COLORS.primitives.greyscale._600};
`

interface SupportContactInformationProps {
	supportId: string
}
const SupportContactInformation = ({
	supportId,
}: SupportContactInformationProps) => {
	const { t } = useTranslation()
	return (
		<SupportContactInformationList>
			<li>
				<InfoLabel id="support-email">
					{t('kebabMenu-contactSupport-email-label')}:{' '}
				</InfoLabel>
				<Link
					aria-describedby="support-email"
					href={`mailto:support@monthio.com?subject=Support id: ${supportId}`}
				>
					support@monthio.com
				</Link>
			</li>
			<li>
				<InfoLabel id="support-call">
					{t('kebabMenu-contactSupport-phone-label')}:{' '}
				</InfoLabel>
				<Link
					aria-describedby="support-call"
					href={`tel:${t('monthio-applicant-support-phone-number')}`}
				>
					{t('monthio-applicant-support-phone-number')}
				</Link>
			</li>
		</SupportContactInformationList>
	)
}

const SupportContactInformationList = styled.ul`
	list-style: none;
	padding: 0;
	margin: 0;
	display: flex;
	flex-direction: column;
	gap: ${SPACING.xs};
`
